import { Button } from '@mui/material';
import { useTranslation } from 'react-i18next';

// The first control of a filter row: a text / date field or a select (MUI's hidden native input is aria-hidden).
const FIRST_CONTROL = 'input:not([type=hidden]):not([aria-hidden=true]), [role=combobox]';

interface ClearFiltersButtonProps {
  /** How many filters are away from their default (`activeCount`). */
  active: number;
  onClear: () => void;
}

/**
 * "Clear filters" (TBL-005): appears only where more than one filter is active — a single one is
 * as quick to undo where it was set — and puts every filter of the list back to its default.
 * Each control away from its default counts once (a date range is two fields, a period select one).
 *
 * The button leaves the page as soon as the filters are clear, which would drop the focus it holds
 * to the body, so focus moves to the first control of the row it sat in.
 */
export function ClearFiltersButton({ active, onClear }: ClearFiltersButtonProps) {
  const { t } = useTranslation();
  if (active < 2) return null;
  return (
    <Button
      size="small"
      onClick={(event) => {
        const row = event.currentTarget.parentElement;
        onClear();
        requestAnimationFrame(() => {
          if (row?.isConnected) row.querySelector<HTMLElement>(FIRST_CONTROL)?.focus();
        });
      }}
    >
      {t('common.clearFilters')}
    </Button>
  );
}
